import '../../../../../../node_modules/emoji-mart/css/emoji-mart.css'

import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import ComposeTemplates from 'components/ReusableComponents/Messaging/Compose/ComposeTemplates'
import ComposeControls from 'components/ReusableComponents/Messaging/ComposeControls'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import { Picker } from 'emoji-mart'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TextareaAutosize from 'react-textarea-autosize'

import { postTyping } from '../../../../actions/messaging'
import { doesSupportEmoji, setCaretPosition } from '../../../../utils/helpers'
import styles from './Compose.module.scss'
import { ReactComponent as CrossIcon } from './Cross.svg'
import { ReactComponent as EmojiIcon } from './emoji.svg'
import { ReactComponent as AttachIcon } from './image.svg'
import { ReactComponent as SendIcon } from './send.svg'

const Compose = ({
  handleSubmit,
  handleClickAddFile,
  chatId,
  disabled = false,
  closed = false,
  filesToSend = [],
  setFilesToSend,
  canShowImagesHolder = true,
  subscriberUtcOffSet,
  isEmailSupported = true,
  setMedium,
  messagingType,
  guestName
}) => {
  const hotelName = useSelector(s => s.hotelDetails.name)
  const [ignoreNotAGoodTimeWarning, setIgnoreNotAGoodTimeWarning] = useState(false)
  const [showNotAGoodTimeWarning, setShowNotAGoodTimeWarning] = useState(false)
  const [isTyping, setTyping] = useState(false)
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState('')
  const [caretPos, setCaretPos] = useState(0)

  const time = subscriberUtcOffSet
    ? moment().utcOffset(subscriberUtcOffSet).format('HH:mm')
    : null

  const isAGoodTimeToSendMessage = time
    ? moment(time, 'HH:mm').isBetween(moment('09:00', 'HH:mm'), moment('22:00', 'HH:mm'))
    : true

  const isInitialRender = useRef(true)
  const dispatch = useDispatch()

  let isDisabled = false

  if (disabled) {
    isDisabled = true
  }

  useEffect(() => {
    setIgnoreNotAGoodTimeWarning(false)
  }, [chatId])

  useEffect(() => {
    if (value.trim().length > 0) {
      setTyping(true)
    } else {
      setTyping(false)
    }
  }, [value])

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
    } else {
      if (chatId) {
        dispatch(postTyping({ chatId, isTyping }))
      }
    }
  }, [chatId, dispatch, isTyping])

  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiContainerRef = useRef()
  const inputRef = useRef()

  const { openUnlockServiceDialog, hasUnlimitedService, hasBasicService } =
    useUnlockService()

  function sendMessage() {
    handleSubmit(value)
    setValue('')
    setFilesToSend([])
  }

  function handleFormSubmit(e) {
    e.preventDefault()

    if (
      !hasUnlimitedService &&
      !hasBasicService &&
      ['service', 'direct'].includes(messagingType)
    ) {
      openUnlockServiceDialog(features.UnlimitedFeatures)
      return
    }

    if (disabled) return

    if (!isAGoodTimeToSendMessage && !ignoreNotAGoodTimeWarning) {
      setShowNotAGoodTimeWarning(true) // business logic
      return
    }

    sendMessage()
  }

  useEffect(() => {
    inputRef?.current?.focus()
    setValue('')
  }, [chatId])

  function handleClickEmojiButton(e) {
    e.preventDefault()
    setShowEmojiPicker(!showEmojiPicker)
    inputRef?.current?.focus()
    setFocused(true)
  }

  function handleSelectEmoji(emoji) {
    setShowEmojiPicker(false)
    setValue(
      [value.substring(0, caretPos), emoji.native, value.substring(caretPos)].join('')
    )

    setTimeout(() => {
      setCaretPosition(inputRef.current, caretPos + emoji.native.length)
    }, 10)
    setFocused(true)
  }

  function handleInputBlur(e) {
    setFocused(false)
    setCaretPos(e.target.selectionStart)
  }

  const handleClickOutsideEmojiButton = useCallback(e => {
    if (emojiContainerRef.current.contains(e.target)) {
      return
    }
    setShowEmojiPicker(false)
  }, [])

  useEffect(() => {
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutsideEmojiButton)
    } else {
      document.removeEventListener('mousedown', handleClickOutsideEmojiButton)
    }

    return () => document.removeEventListener('mousedown', handleClickOutsideEmojiButton)
  }, [handleClickOutsideEmojiButton, showEmojiPicker])

  function handleTextBoxKeyPress(e) {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault()
      handleFormSubmit(e)
    }
  }

  function handleAttachClick(e) {
    e.preventDefault()
    handleClickAddFile()
  }

  return (
    <>
      {showNotAGoodTimeWarning && (
        <ConfirmationDialog
          title={`Guest's local time is ${time}`}
          positiveMessage="Yes, send"
          negativeMessage="No, cancel"
          onConfirm={() => {
            setIgnoreNotAGoodTimeWarning(true)
            setShowNotAGoodTimeWarning(false)
            sendMessage()
          }}
          onClose={() => setShowNotAGoodTimeWarning(false)}
        >
          <p>It might not be a good time for them. Do you still want to send the text?</p>
        </ConfirmationDialog>
      )}
      {!isDisabled && (
        <ComposeTemplates
          setComposeValue={setValue}
          guestName={guestName}
          hotelName={hotelName}
        />
      )}
      {filesToSend.length > 0 && canShowImagesHolder && (
        <div className={styles.imagesHolder}>
          {filesToSend.map(f => (
            <>
              <div style={{ marginRight: 10, position: 'relative' }}>
                <button
                  className={styles.removeMediaButton}
                  onClick={() =>
                    setFilesToSend([...filesToSend.filter(m => m.path !== f.path)])
                  }
                >
                  <CrossIcon
                    fill="white"
                    style={{ filter: 'drop-shadow(rgba(0, 0, 0, 0.48) 0px 1px 2px)' }}
                  />
                </button>
                <FilePreview
                  key={f.url}
                  src={f.url}
                  extension={f.url.match(/\.([a-zA-Z0-9]+)\?X-Amz/)?.[1]?.toLowerCase()}
                  clickable={false}
                  showFileName={true}
                />
              </div>
            </>
          ))}
        </div>
      )}
      <div
        className={[styles.container, isDisabled ? styles.disabledContainer : ''].join(
          ' '
        )}
      >
        <form className={styles.form} onSubmit={handleFormSubmit}>
          <label className={[styles.label, focused ? styles.labelFocused : ''].join(' ')}>
            {isEmailSupported && <ComposeControls medium="sms" setMedium={setMedium} />}
            <TextareaAutosize
              required={filesToSend.length === 0}
              onChange={e => setValue(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={handleInputBlur}
              placeholder={
                closed ? 'Type a message to re-activate this' : 'Type a text message...'
              }
              value={value}
              ref={inputRef}
              disabled={isDisabled}
              maxLength={700}
              onKeyDown={handleTextBoxKeyPress}
            />
            <button className={styles.buttonWithIcon} onClick={handleAttachClick}>
              <AttachIcon />
            </button>
            <div ref={emojiContainerRef}>
              <button
                type="button"
                onClick={handleClickEmojiButton}
                className={styles.buttonWithIcon}
              >
                <EmojiIcon />
              </button>
              <EmojiPicker
                show={showEmojiPicker}
                onSelect={handleSelectEmoji}
                className={styles.emojiPicker}
                winClassName={styles.emojiPickerWin}
              />
            </div>
            <button className={styles.sendButton}>
              <SendIcon />
            </button>
          </label>
        </form>
      </div>
    </>
  )
}

Compose.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  closed: PropTypes.bool,
  chatId: PropTypes.string,
  guestName: PropTypes.string
}

const EmojiPicker = ({ show, className, winClassName, onSelect, ref }) =>
  show ? (
    <div
      className={navigator.appVersion.indexOf('Win') !== -1 ? winClassName : className}
      ref={ref}
    >
      <Picker
        set="apple"
        onSelect={onSelect}
        showPreview={false}
        native={doesSupportEmoji()}
        showSkinTones={false}
        autoFocus={true}
      />
    </div>
  ) : null

export default Compose
